<script>
import Layout from '@/Shared/Layout'
export default { layout: Layout }
</script>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { Carousel, Slide, Pagination } from 'vue3-carousel'
import { Head } from '@inertiajs/inertia-vue3'
import stripslashes, { replaceS3path, filterArrayBySlugs, getGoogleMapURL, removeLineBreaks } from '@/Shared/helper.js'
import ButtonRounded from '@/Shared/Buttons/ButtonRounded'
import ButtonFavoriteToggle from '@/Shared/Buttons/ButtonFavoriteToggle'
import List from '@/Shared/List/List'
import Sidebar from '@/Shared/Sidebar/SidebarBannersNews'
import Breadcrumbs from '@/Shared/Breadcrumbs'
import usePref from '@/Shared/usePref.js'
import useFavorites from '@/Shared/useFavorites.js'

const props = defineProps({
  shopInfo: Array,
  pref: Object,
})

const currentSlide = ref(0)
const onclick = ref()

const cleanup = (str) => replaceS3path(stripslashes(str))

const getShopImages = function () {
  let shopImages = []
  for (let i = 1; i <= 8; i++) {
    const currImage = replaceS3path(props.shopInfo[0]['shopImg' + i])
    if (currImage) shopImages = [...shopImages, currImage]
  }
  return shopImages
}

const relatedBrands = (props.shopInfo[0]?.shopBrand ) ? props.shopInfo[0]?.shopBrand.split(',') : []

const relatedBrandsData = filterArrayBySlugs(props.pref.brands, relatedBrands, 'brandSlug')

onMounted(() => {
  usePref(props.pref)
  const { updateFavoriteList } = useFavorites({ category: 'shop' })
  onclick.value = updateFavoriteList
})
onUnmounted(() => {})
</script>

<template>
  <div class="store-detail">
    <template v-if="shopInfo.length > 0">
      <Head :title="shopInfo[0].shopNameJ">
        <meta name="description" :content="removeLineBreaks(shopInfo[0].shopInfo)" />
        <meta property="og:title" :content="shopInfo[0].shopNameJ" />
        <meta property="og:description" :content="removeLineBreaks(shopInfo[0].shopInfo)" />
        <meta property="og:image" :content="replaceS3path(shopInfo[0].shopThumb)" />
      </Head>

      <div class="detail-container">
        <Breadcrumbs class="!max-w-[800px]" category="shop" :title="shopInfo[0].shopNameJ" />

        <div class="detail-contents">
          <div class="main !max-w-[550px]">
            <carousel id="gallery" v-model="currentSlide" :autoplay="10000" :wrap-around="true" :items-to-show="1" :transition="400" class="brand-product-slide">
              <slide v-for="info in getShopImages()" :key="info">
                <div class="max-w-2xl">
                  <img class="w-screen sm:w-auto" :src="info" :alt="shopInfo[0].shopNameJ" />
                </div>
              </slide>
              <template #addons>
                <pagination />
              </template>
            </carousel>

            <div class="main-contents">
              <h1 class="text-[17px] sm:text-[18px] font-bold mb-[1em] leading-[1.6];">{{ stripslashes(shopInfo[0].shopNameJ) }}</h1>
              <p class="leading-[2.2] mb-[25px] sm:mb-[30px]" v-html="cleanup(shopInfo[0].shopInfo)" />
              <table class="leading-[2] [&_tr]:pb-[10px] [&_th]:whitespace-nowrap [&_th]:align-top [&_th]:font-normal [&_td]:align-top">
                <tr>
                  <th>所在地&emsp;：</th>
                  <td>{{ shopInfo[0].shopCItyJ + shopInfo[0].shopAddress1J + shopInfo[0].shopAddress2J + shopInfo[0].shopAddress3J }}</td>
                </tr>
                <tr>
                  <th>電話番号：</th>
                  <td>{{ shopInfo[0].shopTel }}</td>
                </tr>
                <tr>
                  <th>営業時間：</th>
                  <td v-html="shopInfo[0].shopHours" />
                </tr>
                <tr class="pb-0">
                  <th class="">定休日&emsp;：</th>
                  <td v-html="shopInfo[0].shopHoliday" />
                </tr>
              </table>
              <ul class="py-[40px] sm:py-[60px] mx-[-8px] [&>li]:p-[8px] text-center sm:flex sm:flex-wrap sm:[&>li]:w-1/2">
                <li>
                  <ButtonRounded text="A&amp;Fカントリー<br />公式アプリ" url="https://yappli.plus/aandf_corporatebanner" icon="aandf" />
                </li>
                <li v-if="shopInfo[0].shopInstagram">
                  <ButtonRounded text="インスタグラム" :url="`https://www.instagram.com/${shopInfo[0].shopInstagram}`" icon="instagram" />
                  <div class="text-[11px] mt-[5px]">入荷情報などお知らせします！</div>
                </li>
                <li v-if="shopInfo[0].shopBlogUrl"><ButtonRounded text="ショップブログ" :url="shopInfo[0].shopBlogUrl" icon="external" /></li>
                <li v-if="shopInfo[0].shopRecruitUrl"><ButtonRounded text="アルバイト・求人情報" :url="shopInfo[0].shopRecruitUrl" icon="external" /></li>
                <li><ButtonRounded text="Googleマップ" :url="getGoogleMapURL(shopInfo[0].shopNameJ, shopInfo[0].shopLongtitude, shopInfo[0].shopLangtitude)" icon="external" /></li>
                <li>
                  <div class="btn-rounded" @click="(e) => onclick(e)">
                    <div class="absolute top-1/2 -translate-y-1/2">お気に入り</div>
                    <ButtonFavoriteToggle category="shop" :slug="shopInfo[0].shopSlug" class="relative z-10 w-full h-full [&_.af-icon]:!w-[12px] [&_.af-icon]:!h-[12px]" />
                  </div>
                </li>
              </ul>

              <div class="py-[10px]">
                <div class="list-title !mb-[0]">取り扱いブランド</div>
                <div class="list-contents">
                  <List :list="relatedBrandsData" class="!mt-[10px]" category="brand" type="link" />
                </div>
              </div>
            </div>
          </div>

          <Sidebar category="shops" :slug="props.shopInfo[0].shopSlug" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="grid place-items-center">
        <div class="detail-container">
          <h2 class="page-title !pb-0">
            Stores
            <small>A&amp;F店舗</small>
          </h2>
          <div class="my-16 sm:my-20 md:my-24 lg:my-28">
            <p class="text-[14px] sm:text-[15px] lg:text-[16px] text-center pb-10">
              該当の店舗はありません。
            </p>
            <ButtonRounded class="min-w-[200px] mx-auto" text="A&F店舗一覧を見る" url="/stores" icon="arrow" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
